/**
 * zeqond-clock.ts — local 1.287 Hz tick loop.
 *
 * The HulyaPulse is the substrate's heartbeat. Every Pulse runtime carries
 * a local clock that ticks once every Zeqond (T_Z = 0.777 s). Subscribers
 * register a callback and receive `{ zeqond, phase, t }` once per tick.
 *
 * The clock is a pure derivation from `Date.now() / T_Z`. No network
 * needed. No drift compensation needed. Two devices that both implement
 * this formula tick on the same wall-clock boundaries.
 *
 * Why this matters:
 *   - The orb's visual pulse animation phase-locks to the clock so every
 *     orb in every tab on every device pulses in unison.
 *   - The rejection log uses zeqondLocal + phaseLocal stamps that match
 *     the server-side auditLog rowHash formula bit-for-bit.
 *   - Any future Move that subscribes to `pulse-tick` resolves at exactly
 *     the moment the rest of the framework expects it to.
 */

/** The Zeqond — universal computational tick. Immutable. */
export const T_Z_SECONDS = 0.777;

/** The HulyaPulse frequency. f_H = 1 / T_Z = 1.287… Hz, exactly. */
export const F_H_HZ = 1 / T_Z_SECONDS;

/** Tick payload. */
export interface ZeqondTick {
  /** Integer Zeqonds since Unix epoch. */
  zeqond: number;
  /** Phase ∈ [0, 1) within the current Zeqond. */
  phase: number;
  /** Unix timestamp in milliseconds at the moment of the tick. */
  t: number;
}

type TickListener = (tick: ZeqondTick) => void;

/**
 * Pure formula. Returns the current Zeqond + phase derived from Date.now().
 * Same shape used by:
 *   - shared/api-core/src/lib/auditLog.ts rowHash()
 *   - shared/pulse/src/sandbox/rejection-log.ts
 */
export function nowZeqondTick(): ZeqondTick {
  const t = Date.now();
  const seconds = t / 1000;
  const zeqondFloat = seconds / T_Z_SECONDS;
  const zeqond = Math.floor(zeqondFloat);
  const phase = zeqondFloat - zeqond;
  return { zeqond, phase, t };
}

/**
 * Time in milliseconds until the next Zeqond boundary. Used by
 * ZeqondClock.start() to schedule the next tick precisely.
 */
function msUntilNextZeqond(): number {
  const t = Date.now();
  const T_Z_MS = T_Z_SECONDS * 1000;
  const elapsedInCurrent = t % T_Z_MS;
  return T_Z_MS - elapsedInCurrent;
}

/**
 * Singleton clock. Designed so multiple subscribers (orb, Moves, narrator
 * daemon if it ever runs in-page) share one timer rather than each
 * spinning its own.
 */
class ZeqondClockImpl {
  private listeners: Set<TickListener> = new Set();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;

  /**
   * Subscribe to ticks. Returns an unsubscribe function. The callback is
   * synchronous and must not throw; if it does, the error is logged but
   * other subscribers still fire.
   */
  on(listener: TickListener): () => void {
    this.listeners.add(listener);
    // Auto-start when the first listener attaches.
    if (this.listeners.size === 1 && !this.running) this.start();
    return () => {
      this.listeners.delete(listener);
      // Auto-stop when the last listener leaves.
      if (this.listeners.size === 0) this.stop();
    };
  }

  /** Current tick — equivalent to nowZeqondTick(), exposed on the singleton for ergonomics. */
  now(): ZeqondTick {
    return nowZeqondTick();
  }

  /** Start the timer. Idempotent. Schedules next tick at the next Zeqond boundary. */
  start(): void {
    if (this.running) return;
    this.running = true;
    this.scheduleNext();
  }

  /** Stop the timer. Idempotent. */
  stop(): void {
    if (!this.running) return;
    this.running = false;
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private scheduleNext(): void {
    if (!this.running) return;
    const wait = msUntilNextZeqond();
    this.timer = setTimeout(() => this.tick(), wait);
  }

  private tick(): void {
    if (!this.running) return;
    const t = nowZeqondTick();
    for (const fn of this.listeners) {
      try { fn(t); }
      catch (err) {
        // eslint-disable-next-line no-console
        console.error("[zeqond-clock] listener threw", err);
      }
    }
    // Schedule the NEXT boundary. We don't trust setInterval — even a few ms
    // of timer slop accumulates into visible drift over thousands of ticks.
    // Re-derive from wall clock each time.
    this.scheduleNext();
  }
}

/** The singleton ZeqondClock. Use this everywhere the page needs ticks. */
export const ZeqondClock = new ZeqondClockImpl();

export default ZeqondClock;
